import { FormControl, Grid, Select, type SelectProps } from '@mui/material';
import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { StyledSectionHeading } from './InputLabel';

type AccessibleSelectProps<T = unknown> = {
  label: ReactNode;
  /** Id for the label element and for Select's labelId/aria-labelledby (accessibility). */
  id: string;
  tooltip?: ReactNode;
  endAdornment?: ReactNode;
} & Omit<SelectProps<T>, 'id' | 'labelId' | 'inputProps'> & {
    inputProps?: SelectProps<T>['inputProps'];
  };

/**
 * Label (as span) + MUI Select with correct accessibility (labelId + aria-labelledby).
 */
export function AccessibleSelect<T = unknown>({
  label,
  id,
  tooltip,
  endAdornment,
  inputProps,
  required,
  ...selectProps
}: AccessibleSelectProps<T>) {
  const labelId = `${id}-label`;
  const labelTextId = `${id}-label-text`;
  const inputId = `${id}-input`;

  const selectContent = (
    <FormControl required={required} sx={{ width: '100%' }}>
      <Select<T>
        {...selectProps}
        required={required}
        labelId={labelId}
        inputProps={{
          ...inputProps,
          'aria-labelledby': labelTextId,
          'aria-required': required ? true : undefined,
          id: inputId,
          ...(required && { required: true }),
        }}
      />
    </FormControl>
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <StyledSectionHeading id={labelId}>
          <span id={labelTextId}>{label}</span>
        </StyledSectionHeading>
        {tooltip}
      </Box>
      {endAdornment ? (
        <Grid container>
          <Grid item xs={10}>
            {selectContent}
          </Grid>
          <Grid item xs={1} sx={{ my: 'auto', mx: 'auto' }}>
            {endAdornment}
          </Grid>
        </Grid>
      ) : (
        selectContent
      )}
    </>
  );
}
